import { useState } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Calendar, Clock, Video, MapPin } from "lucide-react";

const timeSlots = ["9:00 AM","9:30 AM","10:00 AM","10:30 AM","11:00 AM","11:30 AM","1:00 PM","1:30 PM","2:00 PM","2:30 PM","3:00 PM","3:30 PM","4:00 PM","4:30 PM"];

export default function AppointmentActions({ appointment, open, onClose, onUpdated }) {
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(false);

  if (!appointment) return null;

  const handleCancel = async () => {
    setLoading(true);
    await API.put(`/appointments/${appointment.id}`, { status: "cancelled" });
    setLoading(false);
    onUpdated();
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) return;
    setLoading(true);
    await API.put(`/appointments/${appointment.id}`, { date: newDate, time: newTime, status: "scheduled" });
    setLoading(false);
    setRescheduleMode(false);
    onUpdated();
  };

  const formattedDate = appointment.date ? new Date(appointment.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="appointment-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">Appointment Details</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="bg-accent rounded-xl p-4">
            <h3 className="font-semibold">{appointment.appointment_type}</h3>
            <p className="text-sm text-muted-foreground">{appointment.provider_name}</p>
            <div className="flex flex-wrap gap-3 mt-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formattedDate}</div>
              <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{appointment.time}</div>
              <div className="flex items-center gap-1.5">{appointment.location === "Telehealth" ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}{appointment.location || "Telehealth"}</div>
            </div>
          </div>
          {rescheduleMode ? (
            <div className="space-y-3">
              <div><Label className="text-sm">New Date</Label><Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="mt-1.5" data-testid="reschedule-date" /></div>
              <div><Label className="text-sm">New Time</Label>
                <Select value={newTime} onValueChange={setNewTime}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select time" /></SelectTrigger><SelectContent>{timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRescheduleMode(false)} className="flex-1 rounded-xl">Cancel</Button>
                <Button onClick={handleReschedule} disabled={!newDate || !newTime || loading} className="flex-1 rounded-xl" data-testid="confirm-reschedule">{loading ? "Saving..." : "Confirm"}</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRescheduleMode(true)} className="flex-1 rounded-xl" data-testid="reschedule-button">Reschedule</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="destructive" className="flex-1 rounded-xl" data-testid="cancel-appointment-button">Cancel</Button></AlertDialogTrigger>
                <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Cancel Appointment?</AlertDialogTitle><AlertDialogDescription>This will cancel your {appointment.appointment_type}. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Keep Appointment</AlertDialogCancel><AlertDialogAction onClick={handleCancel} disabled={loading} data-testid="confirm-cancel">{loading ? "Cancelling..." : "Yes, Cancel"}</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
